import React, { useContext, useEffect, useState, useRef } from 'react'
import { RequestContext } from '../../../../context/RequestContext';
import { FaCheckCircle, FaSearch } from 'react-icons/fa';
import { openModal } from '../../../../service/modal-service';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { BookingCardHeader } from '@/commons/service-hub/BookingCardHeader';
import { buildQuickBookCriteria, getUnitsQuestion, isUnitRequired } from '../../../../utils/handyman-quickbook';
import { HandymanJobDetailsSheet } from './HandymanJobDetailsStep';

export const HandymanBooking = (props) => {
    const setRequestForm = props.setRequestForm
    const setIsQuickBook = props.setIsQuickBook;
    const handymanType = props.handymanType;
    // Default tab per page. The electrician page must not open on plumbing
    // tasks, so it passes defaultServiceType="Electrical".
    const [ serviceType, setServiceType] = useState(props.defaultServiceType || "Bathroom")
    const setIsShowDealItems = props.setIsShowDealItems
    // Optional. Only pass these where the copy is true for that page - this
    // component is shared by the electrician, plumber and handyman pages.
    const title = props.title
    const subtitle = props.subtitle
    // Restrict the visible tabs. e.g. ["Electrical"] on the electrician page.
    const allowedServiceTypes = props.allowedServiceTypes
    const handymanPriceList = props.handymanPriceList
    // Fallback for deciding whether a tile asks for units when the price list
    // has no unitRequired field (see utils/handyman-quickbook.js).
    const requestCategorySettings = props.requestCategorySettings
    // The job whose details sheet is open (summary, materials, units)
    const [ detailsItem, setDetailsItem ] = useState(null)
    // "overlay" (default): translucent dark card for sitting on a hero image.
    // "light": solid white card for plain page backgrounds (price and job pages),
    // where the dark translucent card with white text looked unfinished.
    const isLight = props.variant === 'light'

    const isTabVisible = (label) => !allowedServiceTypes || allowedServiceTypes.includes(label)
    // A single permitted category makes the tab row meaningless - hide it.
    const showTabs = !allowedServiceTypes || allowedServiceTypes.length > 1

    // Resolve price from the live price list by exact item name so the tiles
    // can never drift from the published price table.
    const getPrice = (itemName) => {
        const match = handymanPriceList?.find(i => i.itemName === itemName)
        return match ? match.serviceCost : null
    }

    // Refs and state for horizontal scrolling on mobile
    const handymanBookingScrollRef = useRef(null)
    const [showHandymanBookingArrow, setShowHandymanBookingArrow] = useState(true)

    const { setRequestCriteria, setRequestDescription } = useContext(RequestContext);

    const override = {
        display: "block",
        margin: "auto"
    };
    
    const confirmBook = (criteria) => {
        setRequestCriteria(criteria)
        setRequestDescription('Handyman Service')
        setIsQuickBook(true)
        // setRequestForm(true)
        openModal(setRequestForm)
    }
    
    // Every tile opens the details sheet first. The tile only knows the job
    // name and its tab ("Kitchen", "Electrical", ...); the full entry (category,
    // summary, includesMaterials, unitRequired) comes from the price list.
    const selectTask = (tab, task) => {
        const item = handymanPriceList?.find(i => i.itemName === task) || { itemName: task, category: tab }
        setDetailsItem(item)
    }

    useEffect(() => {
        setRequestCriteria([])
        setRequestDescription('')
        setIsQuickBook(false)
    }, [setIsQuickBook, setRequestCriteria, setRequestDescription])

    // Scroll functions for mobile handyman booking buttons
    const scrollHandymanBookingToNext = () => {
        const scrollContainer = handymanBookingScrollRef.current;
        if (!scrollContainer) return;

        const cardWidth = scrollContainer.querySelector('div')?.offsetWidth || 0;
        scrollContainer.scrollBy({
            left: cardWidth,
            behavior: 'smooth'
        });
    };

    // Scroll detection effect for mobile handyman booking buttons
    useEffect(() => {
        const handymanContainer = handymanBookingScrollRef.current;
        if (handymanContainer) {
            const handleHandymanScroll = () => {
                const { scrollLeft, scrollWidth, clientWidth } = handymanContainer;
                const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 10;
                setShowHandymanBookingArrow(!isAtEnd);
            };

            handymanContainer.addEventListener('scroll', handleHandymanScroll);
            handleHandymanScroll();

            return () => handymanContainer.removeEventListener('scroll', handleHandymanScroll);
        }
    }, [])

    const changeServiceType = (serviceType) => {
        setServiceType(serviceType)
    }

    const getButtonClasses = (label) => {
        const isActive = serviceType === label;
        return [
            'py-2 rounded-xl h-16',
            label === 'Door/Cabinet' ? 'w-28' : 'w-24',
            isActive
            ? 'bg-white ring ring-amber-200 text-black'
            : 'bg-white border text-gray-500 border-gray-200 bg-linear-to-tr from-gray-200 to-white'
        ].join(' ');
    };

    const serviceTasks = {
        Kitchen: [
            ["Fix Water Leak", "Fix water leak from faucet, sink or pipes"],
            ["Clear Clogging", "Clear clogged kitchen sink or pipe"],
            ["Replace Sink", "Replace kitchen sink"],
            ["Replace Faucet", "Replace kitchen faucet"],
            ["Fix Stove/Hob", "Fix stove/hob"],
        ],
        Bathroom: [
            ["Pipe Or Bowl Leak", "Fix water leak from toilet bowl or pipes"],
            ["Clogged Bowl", "Clear clogged toilet bowl"],
            ["Replace Toilet Flush", "Replace toilet flush system"],
            ["Install Toilet Bowl", "Install toilet bowl set"],
            ["New Wash Basin", "Install wash basin"],
        ],
        Electrical: [
            ["Socket / Switch", "Replace power socket or switch"],
            ["Replace Light Bulb", "Replace light bulb"],
            ["Install LED Light", "Install/replace LED light"],
            ["Circuit Breaker", "Replace circuit breaker"],
            ["Install Ceiling Fan", "Install ceiling fan"],
        ],
        "Door/Cabinet": [
            ["Replace Door Lock", "Door/Gate lock replacement"],
            ["Replace Door Knob", "Door handle/knob replacement"],
            ["Fix Cabinet Hinge", "Cabinet/cupboard hinge replacement"],
            ["Window Hinge", "Fix window lock/hinge"],
            ["Window Lock", "Fix window lock/hinge"],
        ],
    }
    
    const QuickBookButton = ({ label, task }) => {
        const price = getPrice(task)
        return (
            <div className="py-2 flex justify-center">
                <button
                    role="button"
                    aria-label={price ? `Quick book: ${label} from $${price.toFixed(2)}` : `Quick book: ${label}`}
                    className="group w-40 h-24 p-3 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
                    onClick={() => selectTask(serviceType, task)}
                >
                    <span className="text-sm font-semibold text-gray-800 leading-tight text-center">{label}</span>
                    {price && (
                        <span className="text-xs font-bold text-white bg-linear-to-tr from-orange-600 to-amber-400 px-2.5 py-1 rounded-full">
                            ${price.toFixed(2)}
                        </span>
                    )}
                </button>
            </div>
        )
    };

    const QuickBookMobileButton = ({ label, task }) => {
        const price = getPrice(task)
        return (
            <div className="w-[29%] inline-block py-2 px-1 align-top">
                <button
                    type="button"
                    className="w-full h-24 p-2 rounded-2xl bg-white border border-gray-200 shadow-sm active:scale-[0.97] transition-all duration-200 flex flex-col items-center justify-center gap-1.5 text-wrap"
                    onClick={() => selectTask(serviceType, task)}
                    aria-label={price ? `Quick book: ${label} from $${price.toFixed(2)}` : `Quick book: ${label}`}
                >
                    <span className="text-sm font-semibold text-gray-800 leading-tight text-center">{label}</span>
                    {price && (
                        <span className="text-sm font-bold text-white bg-linear-to-tr from-orange-600 to-amber-400 px-2 py-0.5 rounded-full">
                            ${price.toFixed(2)}
                        </span>
                    )}
                </button>
            </div>
        )
    };

  return (
    <>
        <div className={isLight
            ? 'py-4 bg-white max-w-md sm:max-w-3xl mx-auto rounded-2xl text-gray-900 border border-gray-200 shadow-md'
            : 'py-4 bg-white sm:bg-black/30 max-w-md sm:max-w-3xl mx-auto rounded-xl sm:text-white shadow'}>            
            <div className='pt-2 relative'>
                {/* The WhatsApp number lives in the page navbar (HandymanNavBar), as on
                    the aircon pages, so the booking card stays focused on picking a job. */}
                {(title || subtitle) &&
                    <BookingCardHeader
                        title={title}
                        subtitle={subtitle}
                        pills={['Upfront fixed pricing']}
                        titleClassName='text-xl sm:text-2xl'
                        subtitleClassName={`text-sm text-gray-600 ${isLight ? '' : 'sm:text-white/90'}`}
                    />
                }
                <div className='px-4 pt-4'>
                    <button
                        className="w-full max-w-md mx-auto px-5 py-3.5 relative flex items-center gap-3 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow"
                        onClick={() => setIsShowDealItems(true)}
                        aria-label="Search for a service"
                    >
                        <FaSearch className='text-lg text-gray-400'/>
                        <span className='text-gray-400 text-sm'>Search a service...</span>
                    </button>
                </div>
                {showTabs &&
                <div className='text-sm flex justify-center space-x-2 pt-5 pb-2 px-1'>
                    {isTabVisible("Bathroom") && (
                        <button
                            aria-label="Select bathroom plumbing service"
                            className={getButtonClasses("Bathroom")}
                            type='button'
                            onClick={() => changeServiceType("Bathroom")}
                        >
                            Bathroom<br />Toilet
                        </button>
                    )}

                    {isTabVisible("Kitchen") && (
                        <button
                            aria-label="Select kitchen plumbing service"
                            className={getButtonClasses("Kitchen")}
                            type='button'
                            onClick={() => changeServiceType("Kitchen")}
                        >
                            Kitchen
                        </button>
                    )}

                    {(handymanType === "Handyman" || handymanType === "Electrician") && isTabVisible("Electrical") && (
                        <button
                        aria-label="Select electrical service"
                        className={getButtonClasses("Electrical")}
                        type='button'
                        onClick={() => changeServiceType("Electrical")}
                        >
                        Electrical
                        </button>
                    )}

                    {isTabVisible("Door/Cabinet") && (
                        <button
                            aria-label="Select door/cabinet repair service"
                            className={getButtonClasses("Door/Cabinet")}
                            type='button'
                            onClick={() => changeServiceType("Door/Cabinet")}
                        >
                            Door/Window<br />Cabinet
                        </button>
                    )}
                </div>
                }
                <div className='pt-3 pb-2'>
                    <div className="hidden sm:grid sm:grid-cols-3 gap-5 text-center w-full pt-5 leading-7">
                        {serviceTasks[serviceType]?.map(([label, task], index) => (
                            <QuickBookButton key={index} label={label} task={task} />
                        ))}

                        <div className="py-2 flex justify-center">
                            <button
                                role="button"
                                aria-label="Browse all services"
                                className="w-40 h-24 p-3 rounded-2xl bg-linear-to-br from-slate-700 to-slate-900 text-white shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 flex flex-col items-center justify-center gap-1 cursor-pointer"
                                onClick={() => setIsShowDealItems(true)}
                                >
                                <span className="text-sm font-semibold">More Services</span>
                                <span className="text-2xs opacity-70">Browse all</span>
                            </button>
                        </div>
                    </div> 
                    <div className="sm:hidden relative">
                        {/* <motion.div
                            className="absolute text-4xl text-amber-400 top-20 w-fit"
                            initial={{ x: "10%", y: "10%" }}
                            animate={{
                            x: ["40px", "180px", "290px", "400px", "290px", "180px", "40px"],
                            y: ["-25%", "0%", "-15%", "0%", "-25%", "10%", "-25%"],
                            }}
                            transition={{ delay: 0.5, ease: "easeInOut", duration: 10, repeat: Infinity }}
                        >
                            <GiClick />
                        </motion.div> */}

                        {serviceTasks[serviceType] && (
                            <div className="relative overflow-hidden">
                                <div
                                    ref={handymanBookingScrollRef}
                                    className="overflow-auto scrollbar-hide whitespace-nowrap text-center text-sm leading-6 w-full"
                                >
                                {serviceTasks[serviceType].map(([label, task], i) => (
                                    <QuickBookMobileButton key={i} label={label} task={task} />
                                ))}
                                </div>
                                {showHandymanBookingArrow && (
                                    <div
                                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer"
                                        onClick={scrollHandymanBookingToNext}
                                    >
                                        <div className='p-1 bg-gray-400 rounded-full shadow-lg animate-pulse'>
                                            <MdKeyboardArrowRight className="text-white text-2xl animate-pulse" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="text-center pb-3 pt-5">
                            <button
                            type="button"
                            className="text-white text-sm font-semibold py-3 px-6 rounded-full bg-linear-to-br from-slate-700 to-slate-900 shadow-sm active:scale-[0.97] transition-all duration-200"
                            onClick={() => setIsShowDealItems(true)}
                            aria-label="Browse all services"
                            >
                            <span>More Services</span>
                            </button>
                        </div>
                    </div>
                    {/* Highlights */}
                    <div className='flex justify-center items-center py-5 w-full text-sm px-2'>
                        <div className='grid grid-cols-2 gap-3'>
                            {['No hidden fees', '30 days warranty'].map((item, i) => 
                                <div key={i} className='flex items-center space-x-2'>
                                    <span className='bg-white text-orange-600 text-xl rounded-full'>
                                    <FaCheckCircle />
                                    </span>
                                    <span>{item}</span>
                                </div>
                            )}
                            </div>
                    </div>
                </div> 
            </div>
        </div>
        {detailsItem &&
            <HandymanJobDetailsSheet
                item={detailsItem}
                askUnits={isUnitRequired(detailsItem, requestCategorySettings)}
                unitsQuestion={getUnitsQuestion(requestCategorySettings, detailsItem.itemName)?.question}
                onClose={() => setDetailsItem(null)}
                onConfirm={(units) => {
                    const item = detailsItem
                    setDetailsItem(null)
                    confirmBook(buildQuickBookCriteria(item, units))
                }}
            />
        }
    </>
  )
}
